import { motion } from 'framer-motion'
import { ArrowLeft } from 'iconsax-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { AppRoutes } from '@/constants/appRoutes'
import type { IReportData, ISocialMediaFound } from '@/hooks/useReportData'
import type { IReportEvent } from '@/hooks/useReportStream'

import { ConsolePanel } from './ConsolePanel'
import { FindingsPanel } from './FindingsPanel'
import { SubjectPanel } from './SubjectPanel'

interface MainViewProps {
  phase: 'init' | 'main'
  events: IReportEvent[]
  reportData: IReportData
  inputProfiles: ISocialMediaFound[]
  isReportCompleted: boolean
}

const TRANSITION_DURATION = 1.6
const TRANSITION_EASE = [0.4, 0, 0.2, 1] as const

export function MainView({
  phase,
  events,
  reportData,
  inputProfiles,
  isReportCompleted,
}: MainViewProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const isMain = phase === 'main'

  return (
    <div className="flex flex-col h-full">
      {isMain && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.6 }}
          className="flex items-center justify-between gap-2 px-6 py-4 flex-shrink-0 bg-baseColorHighlight"
        >
          <button
            onClick={() => navigate(AppRoutes.dashboard)}
            className="flex items-center gap-1.5 text-sm text-neutralColor60 hover:text-neutralColor transition-colors"
          >
            <ArrowLeft size={16} />
            <span>
              {t('dashboard.newSearch.generatingReport.header.backHome')}
            </span>
          </button>

          <div className="flex items-center gap-2 text-xs text-neutralColor60 uppercase tracking-wider">
            <motion.span
              className="w-2 h-2 rounded-full bg-neutralColor60"
              animate={
                isReportCompleted
                  ? { opacity: 1, scale: 1 }
                  : { opacity: [0.3, 1, 0.3], scale: [0.85, 1, 0.85] }
              }
              transition={
                isReportCompleted
                  ? { duration: 0.4 }
                  : { duration: 1.6, repeat: Infinity, ease: 'easeInOut' }
              }
            />
            <span>
              {isReportCompleted
                ? t('dashboard.newSearch.generatingReport.header.complete')
                : t('dashboard.newSearch.generatingReport.header.inProgress')}
            </span>
          </div>
        </motion.div>
      )}

      <div className="flex flex-1 overflow-hidden">
        <motion.div
          initial={false}
          animate={{ width: isMain ? '33.333%' : '100%' }}
          transition={{ duration: TRANSITION_DURATION, ease: TRANSITION_EASE }}
          className="flex-shrink-0 h-full overflow-hidden max-md:!w-full"
        >
          <ConsolePanel events={events} />
        </motion.div>

        {/* h-full + min-h-0 bound the child's height so overflow-y-auto scrolls
            inside the card instead of pushing the page scroll. */}
        <motion.div
          initial={{ width: 0, opacity: 0 }}
          animate={{
            width: isMain ? '33.333%' : 0,
            opacity: isMain ? 1 : 0,
          }}
          transition={{
            width: { duration: TRANSITION_DURATION, ease: TRANSITION_EASE },
            opacity: { delay: 1.0, duration: 0.8 },
          }}
          className="flex-shrink-0 h-full min-h-0 overflow-y-auto scrollbar-hidden max-md:hidden"
        >
          <div className="py-4 px-2">
            <SubjectPanel
              reportData={reportData}
              inputProfiles={inputProfiles}
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ width: 0, opacity: 0 }}
          animate={{
            width: isMain ? '33.333%' : 0,
            opacity: isMain ? 1 : 0,
          }}
          transition={{
            width: { duration: TRANSITION_DURATION, ease: TRANSITION_EASE },
            opacity: { delay: 1.2, duration: 0.8 },
          }}
          className="flex-shrink-0 h-full min-h-0 overflow-hidden max-md:hidden"
        >
          <FindingsPanel findings={reportData.findings} />
        </motion.div>
      </div>
    </div>
  )
}
